import { Divider } from "react-native-paper";
import { Link } from "@react-navigation/native";
import {
    Button,
    Headline,
    NumberInput,
    Spacer,
    StyleFunction,
    Table,
    Text,
    TooltipWarningIcon,
    useTheme,
    useThemedStyle,
} from "@venuepos/react-common";
import {
    formatDateTime,
    IAccountChartByProductGroup,
    IProductGroup,
} from "lib";
import { useTranslation } from "react-i18next";
import React, { View } from "react-native";
import { AvailableLocale } from "locales";
import { useAdminSession } from "../../../session";

export function AccountChartByProductGroup({
    productGroupMap,
    onUpdateMap,
    onSave,
}: {
    productGroupMap?: IAccountChartByProductGroup[];
    onUpdateMap: (
        productGroupId: IProductGroup["id"],
        accountNo: number | null
    ) => void;
    onSave: () => void;
}) {
    const [{ locale }] = useAdminSession(["locale"]);

    const theme = useTheme();
    const styles = useThemedStyle(styleFunc);
    const [t] = useTranslation();

    if (!productGroupMap) {
        return null;
    }

    return (
        <View style={styles.container}>
            <Headline size="h4">
                {t(
                    "backoffice.integration.economic.account_chart_by_product_group",
                    "Account mapping for product groups"
                )}
            </Headline>
            <View>
                <Text>
                    {t(
                        "backoffice.integration.economic.account_chart_by_product_group_description",
                        "When the sale is summed up for bookkeeping, it is spread on the below account numbers related to the product groups.\n\nIf a product group does not have an account number, then the general 'Salg af ydelser' account number above will be used."
                    )}
                </Text>
            </View>
            <Spacer />
            <Divider />
            <Table>
                <Table.Header>
                    <Table.Title>
                        {t("common.product_group", "Product group")}
                    </Table.Title>
                    <Table.Title numeric style={styles.inputRow}>
                        {t("common.account_number", "Account number")}
                    </Table.Title>
                </Table.Header>
                {productGroupMap?.map((item, idx) => {
                    // If the product group in the list is deleted, and there is no account for it, then display nothing.
                    if (!item.accountNo && item.productGroup.deletedAt) {
                        return null;
                    }

                    return (
                        <Table.Row
                            style={
                                idx % 2 === 0 ? theme.styles.oddRow : undefined
                            }
                            key={`product_group_map_${item.productGroup.id}`}
                        >
                            <Table.Cell>
                                {!item.productGroup.deletedAt ? (
                                    <Link
                                        to={{
                                            screen: "PRODUCT_GROUP_EDIT",
                                            params: {
                                                id: item.productGroup.id,
                                            },
                                        }}
                                        style={theme.styles.link}
                                    >
                                        {item.productGroup.name}
                                    </Link>
                                ) : (
                                    <>
                                        <Text>{item.productGroup.name}</Text>
                                        <TooltipWarningIcon
                                            title={`${t(
                                                "backoffice.group.deleted_at",
                                                "Group was deleted at"
                                            )} ${formatDateTime(
                                                item.productGroup.deletedAt,
                                                locale as AvailableLocale
                                            )}`}
                                        />
                                    </>
                                )}
                            </Table.Cell>
                            <Table.Cell numeric style={styles.inputRow}>
                                <NumberInput
                                    value={item.accountNo}
                                    onChange={newValue =>
                                        onUpdateMap(
                                            item.productGroup.id,
                                            newValue
                                        )
                                    }
                                    min={0}
                                    max={100000} // Best guess based on the account chart from e-conomic: 100.000 is enough for most uses...
                                    style={styles.input}
                                />
                            </Table.Cell>
                        </Table.Row>
                    );
                })}
            </Table>
            <Spacer space={2} />
            <Button onPress={onSave}>{t("common.save", "Save")}</Button>
        </View>
    );
}

const styleFunc: StyleFunction = theme => ({
    container: {
        width: "100%",
        maxWidth: theme.dimensions.maxFormWidth,
        alignSelf: "flex-start",
    },

    input: {
        marginBottom: 0,
    },
    inputRow: {
        flexBasis: 160,
        flexShrink: 0,
        flexGrow: 0,
    },
});
